import React from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Input, Select, Space, Table } from 'antd';
import { AdminAccountDeletionRequestDto, AccountDeletionRequestStatus } from '@wellverse/shared-ts';
import { STATUS_OPTIONS, StatusTag } from './labels';

/** Requests from the website form, newest first. Opens on the pending ones. */
export const AccountDeletionRequestList: React.FC = () => {
  const { tableProps, filters, setFilters } = useTable<AdminAccountDeletionRequestDto>({
    resource: 'account-deletion-requests',
    pagination: { current: 1, pageSize: 20 },
    filters: { initial: [{ field: 'status', operator: 'eq', value: 'pending' }] },
  });
  const status = filters.find((f) => 'field' in f && f.field === 'status')?.value as
    | AccountDeletionRequestStatus
    | undefined;
  // A reference is looked up across every status.
  const findReference = (reference: string) =>
    setFilters(
      reference.trim()
        ? [
            { field: 'reference', operator: 'eq', value: reference.trim() },
            { field: 'status', operator: 'eq', value: undefined },
          ]
        : [{ field: 'reference', operator: 'eq', value: undefined }],
      'merge',
    );

  return (
    <List
      resource="account-deletion-requests"
      title="Account Deletion Requests"
      headerButtons={
        <Space>
          <Input.Search
            style={{ width: 240 }}
            allowClear
            placeholder="Reference, e.g. WKD-20261021-0007"
            onSearch={findReference}
          />
          <Select
            style={{ width: 160 }}
            allowClear
            placeholder="All statuses"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(value) => setFilters([{ field: 'status', operator: 'eq', value }], 'replace')}
          />
        </Space>
      }
    >
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="reference" title="Reference" />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(value: AccountDeletionRequestStatus) => <StatusTag status={value} />}
        />
        <Table.Column
          title="Contact"
          render={(_, record: AdminAccountDeletionRequestDto) => record.email ?? record.phone}
        />
        <Table.Column
          dataIndex="userId"
          title="Matched account"
          render={(userId: string | undefined, record: AdminAccountDeletionRequestDto) =>
            userId ? record.userName ?? userId : '— no match —'
          }
        />
        <Table.Column
          dataIndex="createdAt"
          title="Received"
          render={(date: string) => new Date(date).toLocaleString()}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: AdminAccountDeletionRequestDto) => (
            <ShowButton hideText size="small" recordItemId={record.id} resource="account-deletion-requests" />
          )}
        />
      </Table>
    </List>
  );
};
